import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { Company } from '../types';
import SidebarPanel from './ui/SidebarPanel';

interface AddCompanySidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddCompanySidebar: React.FC<AddCompanySidebarProps> = ({ isOpen, onClose }) => {
  const { addCompany } = useCRM();
  
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    phone: '',
    email: '',
    address: '',
    status: 'Prospect' as Company['status'],
    accountOwner: '',
    dealValue: '',
    employees: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const industryOptions = [
    'Technology',
    'Healthcare',
    'Manufacturing',
    'Financial Services',
    'Retail',
    'Education',
    'Real Estate',
    'Consulting',
    'Other'
  ];

  const sizeOptions = [
    '1-10',
    '11-50',
    '51-100',
    '101-500',
    '501-1000',
    '1000+'
  ];

  const accountOwners = [
    'Sarah Chen',
    'Carol Carter',
    'Alex Drago',
    'Frank Wright',
    'Sarah Mitchell',
    'Marcus Johnson',
    'Priya Patel',
    'David Rodriguez',
    'Maria Garcia',
    'James Wilson'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }

    if (!formData.size) {
      newErrors.size = 'Company size is required';
    }

    if (!formData.accountOwner) {
      newErrors.accountOwner = 'Account owner is required';
    }

    if (formData.website && !formData.website.includes('.')) {
      newErrors.website = 'Please enter a valid website';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const companyData: Omit<Company, 'id'> = {
      ...formData,
      employees: formData.employees ? parseInt(formData.employees) : undefined,
      lastActivity: new Date().toLocaleString(),
      dealValue: formData.dealValue || '$0'
    };

    addCompany(companyData);
    
    // Reset form
    setFormData({
      name: '',
      website: '',
      industry: '',
      size: '',
      phone: '',
      email: '',
      address: '',
      status: 'Prospect',
      accountOwner: '',
      dealValue: '',
      employees: ''
    });
    
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      website: '',
      industry: '',
      size: '',
      phone: '',
      email: '',
      address: '',
      status: 'Prospect',
      accountOwner: '',
      dealValue: '',
      employees: ''
    });
    setErrors({});
    onClose();
  };

  const renderInput = (
    label: string,
    name: keyof typeof formData,
    type: string = 'text',
    required: boolean = false,
    options?: string[],
    placeholder?: string
  ) => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={name}
          name={name}
          value={formData[name] as string}
          onChange={handleInputChange}
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
            errors[name] ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={placeholder}
        />
      ) : type === 'select' ? (
        <select
          id={name}
          name={name}
          value={formData[name] as string}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white ${
            errors[name] ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">{placeholder || `Select ${label.toLowerCase()}`}</option>
          {options?.map(option => (
            <option key={option} value={option}>
              {name === 'size' ? `${option} employees` : option}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name] as string}
          onChange={handleInputChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
            errors[name] ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={placeholder}
          min={type === 'number' ? '1' : undefined}
        />
      )}
      {errors[name] && <p className="mt-1 text-sm text-red-600">{errors[name]}</p>}
    </div>
  );

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Company"
      width="xl"
      headerColor="bg-teal-500"
      headerTextColor="text-white"
    >
      <div className="p-6 pb-24">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Company Name', 'name', 'text', true, undefined, 'Enter company name')}
                {renderInput('Website', 'website', 'text', false, undefined, 'www.company.com')}
                {renderInput('Email', 'email', 'email', true, undefined, 'contact@company.com')}
                {renderInput('Phone', 'phone', 'tel', false, undefined, '+1 (555) 123-4567')}
              </div>
            </div>

            {/* Company Details Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Company Details
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Industry', 'industry', 'select', true, industryOptions, 'Select industry')}
                {renderInput('Company Size', 'size', 'select', true, sizeOptions, 'Select size')}
                {renderInput('Number of Employees', 'employees', 'number', false, undefined, '100')}
                {renderInput('Deal Value', 'dealValue', 'text', false, undefined, '$100,000')}
              </div>
            </div>

            {/* Account Management Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Account Management
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {renderInput('Account Owner', 'accountOwner', 'select', true, accountOwners, 'Select account owner')}
                {renderInput('Status', 'status', 'select', false, ['Prospect', 'Active', 'Inactive'])}
              </div>
            </div>

            {/* Additional Information Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Additional Information
              </h3>
              {renderInput('Address', 'address', 'textarea', false, undefined, '123 Main Street, City, State, ZIP')}
            </div>
          </div>

          {/* Fixed Footer with Action Buttons */}
          <div className="fixed bottom-0 right-0 bg-white border-t border-gray-200 p-4 w-[40rem] flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
            >
              Add Company
            </button>
          </div>
        </form>
      </div>
    </SidebarPanel>
  );
};

export default AddCompanySidebar;
